import React, { useState } from "react";
import { useUserData } from "components/customHooks/userDataContext";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import notesTemplates from "components/scribe/notesTemplates";
import ButtonProgress from "components/ButtonProgress";
import { Box, Button, TextField } from "@mui/material";

const PatientNotes = ({
  id,
  practice_id,
  encounter_id,
  npi,
  savedNotes,
  onClose,
  updateNotes,
  categories,
  fetchedNotes,
}) => {
  const uniqueCategories = Array.from(new Set(categories || []));
  const categoriesData =
    uniqueCategories && uniqueCategories.length > 0 ? uniqueCategories[0] : "";

  const [current, setCurrentNote] = useState(categoriesData);
  const [notes, setNotes] = useState({ ...savedNotes });
  const [showTemplate, setShowTemplate] = useState(false);
  const [selTemplate, setSelTemplate] = useState(null);
  const [searchTemp, setSearchTemp] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const userData = useUserData();

  // Find and Replace State
  const [findText, setFindText] = useState("");
  const [replaceText, setReplaceText] = useState("");

  const addTemplate = (text) => {
    if (!text) return;
    setNotes({ ...notes, [current]: notes[current] + text });
  };

  const handleReplaceAll = () => {
    if (!findText) return;
    const regex = new RegExp(findText, "gi");
    setNotes((prevNotes) => ({
      ...prevNotes,
      [current]: prevNotes[current].replaceAll(regex, replaceText),
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    const common = {
      dictation_id: id,
      user_name: userData.first_name + " " + userData.last_name,
      scribe_id: userData.email,
      encounter_id,
      practice_id,
      npi,
    };
    let payloadNotes;
    if (fetchedNotes) {
      payloadNotes = fetchedNotes.map((n) => ({
        ...n,
        note_text: notes[n.note_category] || "",
        practice_id: userData?.selected_practice,
        user_name: userData.first_name + " " + userData.last_name,
      }));
    } else {
      payloadNotes = uniqueCategories.map((c) => ({
        ...common,
        note_category: c,
        note_text: notes[c] || "",
        practice_id: userData?.selected_practice,
      }));
    }
    if (payloadNotes.length === 0) {
      onClose();
      setIsSaving(false);
      return;
    }
    const payload = {
      type: "Scribe",
      notes: payloadNotes,
    };
    let subjective;
    let assessment;
    if (
      Object.prototype.hasOwnProperty.call(notes, "Subjective") ||
      Object.prototype.hasOwnProperty.call(notes, "Assessment")
    ) {
      subjective =
        notes?.["Subjective"] === savedNotes?.["savedNotes"]
          ? ""
          : notes?.["Subjective"];
      assessment =
        notes?.["Assessment"] === savedNotes?.["Assessment"]
          ? ""
          : notes?.["Assessment"];
    } else if (
      Object.prototype.hasOwnProperty.call(notes, "History") ||
      Object.prototype.hasOwnProperty.call(notes, "Recommendation")
    ) {
      subjective =
        notes?.["History"] === savedNotes?.["History"]
          ? ""
          : notes?.["History"];
      assessment =
        notes?.["Recommendation"] === savedNotes?.["Recommendation"]
          ? ""
          : notes?.["Recommendation"];
    }
    if (assessment && subjective)
      await updateNotes(payload, assessment, subjective);
    else if (subjective) await updateNotes(payload, "", subjective);
    else if (assessment) await updateNotes(payload, assessment, "");
    else await updateNotes(payload, "", "");
    setIsSaving(false);
  };

  return (
    <div>
      <header className="dnm-header">
        <h3 className="dnm-header-title">{current}</h3>
        <div>
          <button
            onClick={() => {
              setShowTemplate(!showTemplate);
              if (selTemplate) setSelTemplate(null);
            }}
            className="dnm-header-template"
          >
            Templates
          </button>
          <button className="dnm-header-save" onClick={handleSave}>
            {isSaving ? <ButtonProgress size={14} /> : "Save"}
          </button>
          <button className="dnm-header-cancel" onClick={onClose}>
            Cancel
          </button>
        </div>
      </header>

      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <TextField
          label="Find Text"
          value={findText}
          onChange={(e) => setFindText(e.target.value)}
          size="small"
        />
        <TextField
          label="Replace Text"
          value={replaceText}
          onChange={(e) => setReplaceText(e.target.value)}
          size="small"
        />
        <Button onClick={handleReplaceAll} size="small">
          Replace All
        </Button>
      </Box>

      <nav className="dnm-btn-cont">
        {uniqueCategories &&
          uniqueCategories.map((c) => (
            <button
              className={`dnm-note-btn ${current === c ? "dnm-note-sel" : ""}`}
              onClick={() => setCurrentNote(c)}
              key={c}
            >
              {c}
            </button>
          ))}
      </nav>
      <main className="dnm-main">
        <div className="dnm-input-cont">
          <div className="dnm-input-div">
            {uniqueCategories &&
              uniqueCategories.map((c) => (
                <div
                  style={{ display: current === c ? "block" : "none" }}
                  key={c}
                >
                  <ReactQuill
                    theme="snow"
                    value={notes[c] || ""}
                    onChange={(e) =>
                      setNotes((prevNotes) => ({
                        ...prevNotes,
                        [c]: e,
                      }))
                    }
                  />
                </div>
              ))}
          </div>
        </div>
        {showTemplate && (
          <div className="dnm-template">
            <h4 className="dnm-temp-title">Templates</h4>
            {selTemplate && (
              <div className="dnm-btn-cont">
                <button
                  onClick={() => {
                    setSelTemplate(null);
                    setSearchTemp("");
                  }}
                  className="dnm-temp-back"
                >
                  Back
                </button>
                <button
                  onClick={() =>
                    addTemplate(selTemplate?.values?.join("<br />"))
                  }
                  className="dnm-temp-save"
                >
                  Add All
                </button>
              </div>
            )}
            <input
              value={searchTemp}
              onChange={(e) => setSearchTemp(e.target.value)}
              placeholder="Search Template"
              className="dnm-temp-search"
            />
            <div className="dnm-temp-cont">
              {!selTemplate ? (
                notesTemplates.map((t, i) => {
                  if (searchTemp) {
                    let reg = new RegExp(searchTemp, "gi");
                    if (!reg.test(t.label)) return null;
                  }
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        setSearchTemp("");
                        setSelTemplate({
                          label: t.label,
                          values: t.values,
                        });
                      }}
                      className="dnm-temp-label"
                    >
                      {t.label}
                    </button>
                  );
                })
              ) : (
                <div className="dnm-temp-values">
                  <h4 className="dnm-val-label">{selTemplate.label}</h4>
                  {selTemplate?.values?.map((t, i) => (
                    <button
                      key={i}
                      onClick={() => addTemplate(t)}
                      className="dnm-val-text"
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PatientNotes;
